/**
 * FixAction — the Fix button on an Issues v6 issue, and the modal behind it.
 * The legacy IssueFixAction, pointed at the new store (heal_sessions.issue_v6_id);
 * a copy, so the legacy one can be deleted as a file at cutover.
 *
 * One button, sitting next to Mark closed, because starting a fix and closing an
 * issue are the two things you do TO an issue and they belong together. The
 * detail — which agent, any extra instructions — lives in the modal rather than
 * on the page, so the popup is not carrying a form for something you mostly are
 * not doing.
 *
 * There is no `acknowledged` status in V5 — the lifecycle is open / closed /
 * regression and nothing else. "Being fixed" is DERIVED from a linked heal
 * session in `queued` or `triggered`, which is also why the button disables
 * itself instead of a second one ever being pressable. The backend still
 * enforces it with a 409; this is the half the user sees.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Wrench, X, ChevronRight } from 'lucide-react';
import { getConnectedAgents } from 'features/agent-analytics/components/heal/healApi';
import { triggerIssueFix, listIssueFixes } from 'features/issues/api/issuesApi';
import usePolling from 'shared/hooks/usePolling';
import { ClaudeCodeMark, CursorMark } from 'features/issues/components/AgentMarks';

const POLL_MS = 4000;
const ASSET = (process.env.PUBLIC_URL || '').replace(/\/?$/, '');

const AGENTS = [
  { key: 'cursor', label: 'Cursor', icon: `${ASSET}/icons/cursor.svg`, Mark: CursorMark },
  { key: 'claudecode', label: 'Claude Code', icon: `${ASSET}/icons/claude.png`, Mark: ClaudeCodeMark },
];

const iconBtnCls = 'inline-flex h-7 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-[color:var(--border-default)] '
  + 'bg-surface px-2 text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:cursor-not-allowed '
  + 'disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

/** Both agents connected: one button, a small menu on hover or click. */
function SolveMenu({ agents, onPick }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const hold = () => { clearTimeout(timer.current); setOpen(true); };
  const release = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 150); };
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <div className="relative" onMouseEnter={hold} onMouseLeave={release}>
      <button type="button" className={iconBtnCls} aria-haspopup="menu" aria-expanded={open} aria-label="Solve with a coding agent" title="Solve with a coding agent" onClick={() => setOpen((o) => !o)}>
        {agents.map((a) => <a.Mark key={a.key} size={14} />)}
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 top-full z-50 pt-1">
          <div className="min-w-[190px] rounded-md border border-[color:var(--border-default)] bg-surface-raised p-1 shadow-token-md">
            {agents.map((a) => (
              <button key={a.key} type="button" role="menuitem" onClick={() => { setOpen(false); onPick(a.key); }}
                className="flex h-8 w-full items-center gap-2 rounded px-2.5 text-left text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover">
                <a.Mark size={14} />
                Solve with {a.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const btnCls = 'inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-[color:var(--border-default)] '
  + 'bg-surface px-2.5 text-[11px] font-medium text-[color:var(--text-primary)] transition-colors '
  + 'hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none '
  + 'focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

function Modal({ open, onClose, title, children }) {
  // Capture phase, and it stops the event dead. The issue drawer this modal
  // opens on top of has its own document-level Escape handler, registered
  // first — so a plain bubble-phase listener here means one Escape closes the
  // modal AND the drawer behind it, losing the issue the user was reading.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-[420px] overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-surface shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-4 py-3">
          <h3 className="text-[13px] font-semibold text-[color:var(--text-primary)]">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-6 w-6 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function AgentTile({ agent, onClick, selected }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] ${
        selected
          ? 'border-[color:var(--brand-primary)] bg-surface-hover'
          : 'border-[color:var(--border-default)] bg-surface hover:bg-surface-hover'
      }`}
    >
      <img src={agent.icon} alt="" className="h-6 w-6 shrink-0 object-contain" />
      <span className="text-[13px] font-medium text-[color:var(--text-primary)]">{agent.label}</span>
      <ChevronRight size={14} className="ml-auto shrink-0 text-[color:var(--text-muted)]" />
    </button>
  );
}

export default function FixAction({ teamId, issue, teamSlug, onSessionsChanged }) {
  const issueId = issue?.id;
  const navigate = useNavigate();
  const [agents, setAgents] = useState(null);   // null = still loading
  const [activeId, setActiveId] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('agent');    // 'agent' | 'instructions'
  const [source, setSource] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!teamId || !issueId) return;
    try {
      const { activeSessionId } = await listIssueFixes(teamId, issueId);
      setActiveId(activeSessionId);
      onSessionsChanged?.();
    } catch {
      // A history that will not load must not take the button down with it.
    }
  }, [teamId, issueId, onSessionsChanged]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!teamId) return undefined;
    let alive = true;
    getConnectedAgents(teamId)
      .then((a) => { if (alive) setAgents(a); })
      .catch(() => { if (alive) setAgents({ cursor: false, claudecode: false }); });
    return () => { alive = false; };
  }, [teamId]);

  // Poll only while something is genuinely in flight. A finished run has nothing
  // left to say, and polling it forever is a request every four seconds for as
  // long as the popup stays open. A coding agent takes minutes, so the delay
  // grows to 30s; nothing runs while the tab is hidden.
  usePolling(refresh, { enabled: Boolean(activeId), interval: POLL_MS, maxInterval: 30000, immediate: false, key: activeId });

  const connected = useMemo(
    () => AGENTS.filter((a) => agents?.[a.key]),
    [agents],
  );

  // The button already chose the agent (one connected, or picked from the
  // menu), so this goes straight to the part that needs a person.
  const openFix = useCallback((key) => {
    setError('');
    setInstructions('');
    setSource(key);
    setStep('instructions');
    setOpen(true);
  }, []);

  const goToSetup = useCallback((key) => {
    navigate(`/${encodeURIComponent(teamSlug || '')}/settings/integrations?integration=${key}`);
  }, [navigate, teamSlug]);

  const start = useCallback(async () => {
    if (!source) return;
    setStarting(true);
    setError('');
    try {
      await triggerIssueFix(teamId, {
        source,
        issueId,
        extraInstructions: instructions.trim() || null,
      });
      setOpen(false);
      await refresh();
    } catch (err) {
      // Surface it. A trigger that failed silently leaves the user looking at a
      // button that did nothing, which is worse than an error.
      const res = err?.response;
      setError(res?.data?.error || err?.message || 'Could not start the fix.');
      if (res?.status === 409) await refresh();
    } finally {
      setStarting(false);
    }
  }, [source, teamId, issueId, instructions, refresh]);

  if (!issueId) return null;

  const noAgents = agents && connected.length === 0;

  return (
    <>
      {activeId ? (
        <button type="button" disabled className={iconBtnCls} title="Fixing…" aria-label="Fixing…"><Loader2 size={13} className="animate-spin" /></button>
      ) : agents === null ? (
        <button type="button" disabled className={iconBtnCls} aria-label="Loading coding agents"><Loader2 size={13} className="animate-spin" /></button>
      ) : noAgents ? (
        <button type="button" onClick={() => setOpen(true)} className={btnCls}>Connect Agent</button>
      ) : connected.length === 1 ? (
        <button type="button" onClick={() => openFix(connected[0].key)} className={iconBtnCls} title={`Solve with ${connected[0].label}`} aria-label={`Solve with ${connected[0].label}`}>
          {React.createElement(connected[0].Mark, { size: 14 })}
        </button>
      ) : (
        <SolveMenu agents={connected} onPick={openFix} />
      )}

      {/* No agent connected: pick one and go set it up. */}
      <Modal open={open && noAgents} onClose={() => setOpen(false)} title="Connect a coding agent">
        <div className="space-y-2">
          {AGENTS.map((a) => (
            <AgentTile key={a.key} agent={a} onClick={() => goToSetup(a.key)} />
          ))}
        </div>
      </Modal>

      {/* Step 1: which agent. Skipped when only one is connected. */}
      <Modal open={open && !noAgents && step === 'agent'} onClose={() => setOpen(false)} title="Choose a coding agent">
        <div className="space-y-2">
          {connected.map((a) => (
            <AgentTile
              key={a.key}
              agent={a}
              selected={source === a.key}
              onClick={() => { setSource(a.key); setStep('instructions'); }}
            />
          ))}
        </div>
      </Modal>

      {/* Step 2: anything to add, then go. */}
      <Modal open={open && !noAgents && step === 'instructions'} onClose={() => setOpen(false)} title="Fix this issue">
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={4}
          autoFocus
          placeholder="Extra instructions (optional)"
          className="w-full resize-none rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2 text-[13px] leading-relaxed text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] outline-none transition-colors focus:border-[color:var(--text-primary)]"
        />

        {error ? (
          <div className="mt-3 rounded-md border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-2 text-[11px] text-[color:var(--feedback-error)]">
            {error}
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-end gap-2">
          {connected.length > 1 ? (
            <button type="button" onClick={() => setStep('agent')} className={btnCls}>
              Back
            </button>
          ) : null}
          <button type="button" onClick={start} disabled={starting} className={btnCls}>
            {starting ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
            {starting ? 'Starting…' : 'Fix'}
          </button>
        </div>
      </Modal>
    </>
  );
}
